import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchTimeSlots, timeSlotsKeys } from './useTimeSlots';
import type { ServiceType } from '../state/types';
import type { TimeSlotsResponse } from '../types/timeSlots';

export interface WaitingListHourOption {
  value: string;
  label: string;
  serviceType: string;
}

interface UseWaitingListHoursOptions {
  restaurantId: number | undefined;
  partySize: number;
  date: string | null;
  serviceTypes: ServiceType[];
  enabled?: boolean;
  meitreNetworkMenuId?: number | null;
  areaFilterIds?: number[] | null;
  /** When true, dedupe hours across services (use when user has not picked a service yet). */
  dedupByHourOnly?: boolean;
}

interface UseWaitingListHoursResult {
  hours: WaitingListHourOption[];
  isLoading: boolean;
  isSettled: boolean;
  error: Error | null;
}

export function formatHourLabel(hour: string): string {
  const [hStr, mStr] = hour.split(':');
  const h = Number(hStr);
  if (!Number.isFinite(h) || !mStr) return hour;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mStr.padStart(2, '0')} ${period}`;
}

export function buildHourOptionsFromResponses(
  responses: Array<TimeSlotsResponse & { serviceType: ServiceType }>,
  options: { dedupByHourOnly?: boolean } = {},
): WaitingListHourOption[] {
  const seen = new Map<string, WaitingListHourOption>();
  for (const data of responses) {
    for (const slot of data.center?.slots ?? []) {
      const key = options.dedupByHourOnly ? slot.hour : `${data.serviceType}|${slot.hour}`;
      if (seen.has(key)) continue;
      seen.set(key, {
        value: slot.hour,
        label: formatHourLabel(slot.hour),
        serviceType: data.serviceType,
      });
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.value.localeCompare(b.value));
}

export function useWaitingListHours({
  restaurantId,
  partySize,
  date,
  serviceTypes,
  enabled = true,
  meitreNetworkMenuId,
  areaFilterIds,
  dedupByHourOnly = false,
}: UseWaitingListHoursOptions): UseWaitingListHoursResult {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';

  const queries = useQueries({
    queries: serviceTypes.map((serviceType) => ({
      queryKey: timeSlotsKeys.byService(
        restaurantId!,
        partySize,
        date!,
        serviceType,
        lang,
        meitreNetworkMenuId ? [meitreNetworkMenuId] : undefined,
        meitreNetworkMenuId ? undefined : (areaFilterIds ?? undefined),
      ),
      queryFn: () =>
        fetchTimeSlots(lang, restaurantId!, partySize, date!, serviceType, undefined, meitreNetworkMenuId, areaFilterIds),
      enabled: enabled && !!restaurantId && partySize > 0 && !!date && serviceTypes.length > 0,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    })),
  });

  const hours = useMemo<WaitingListHourOption[]>(() => {
    const responses: Array<TimeSlotsResponse & { serviceType: ServiceType }> = [];
    queries.forEach((q) => {
      if (!q.isSuccess || !q.data) return;
      responses.push(q.data as TimeSlotsResponse & { serviceType: ServiceType });
    });
    return buildHourOptionsFromResponses(responses, { dedupByHourOnly });
  }, [queries, dedupByHourOnly]);

  const isLoading = queries.some((q) => q.isLoading);
  const isSettled = queries.length === 0 || queries.every((q) => q.isSuccess || q.isError);
  const error = useMemo(() => {
    const failed = queries.find((q) => q.isError);
    return (failed?.error as Error | null) ?? null;
  }, [queries]);

  return { hours, isLoading, isSettled, error };
}
